import React from "react";

import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import { trackEvent } from "@shared/lib/posthog";

type Props = {
  onCancel: () => void;
  currentPlanName: string;
  userLimit: number;
  activeUserCount: number;
  nextPlanName?: string;
  hasPendingInvites?: boolean;
  hasMultipleActiveUsers?: boolean;
  location: any;
  history: any;
};

function UpgradeUserLimitModal(props: Props) {
  const {
    currentPlanName,
    userLimit,
    activeUserCount,
    nextPlanName,
    hasPendingInvites = false,
    hasMultipleActiveUsers = false,
  } = props;
  // window.logger("%c[UpgradeUserLimitModal] RENDER ", "color: #207c3f", {
  //   currentPlanName,
  //   userLimit,
  //   activeUserCount,
  //   nextPlanName,
  // });

  // Generate appropriate message based on available options
  const getSecondaryMessage = () => {
    const options = [];

    // Always suggest upgrading
    if (nextPlanName) {
      options.push(`upgrade to the ${nextPlanName} plan`);
    } else {
      options.push("upgrade your plan");
    }

    // Suggest canceling invites if there are pending invites
    if (hasPendingInvites) {
      options.push("cancel pending invitations");
    }

    // Suggest deactivating users if there are multiple active users
    if (hasMultipleActiveUsers) {
      options.push("deactivate an existing user");
    }

    // For Free plan (limit of 1), only suggest upgrading
    if (userLimit === 1) {
      return nextPlanName
        ? `To add more active team members, please upgrade to the ${nextPlanName} plan.`
        : "To add more team members, please upgrade your plan.";
    }

    // Build message based on available options
    if (options.length === 1) {
      return `To add more team members, please ${options[0]}.`;
    } else if (options.length === 2) {
      return `To add more team members, please ${options[0]} or ${options[1]}.`;
    } else if (options.length === 3) {
      return `To add more team members, please ${options[0]}, ${options[1]}, or ${options[2]}.`;
    }

    return "To add more team members, please upgrade your plan.";
  };

  const handleViewPlans = () => {
    trackEvent("upgrade_plan_view_plans_clicked", { current_plan: currentPlanName, trigger: "user_limit" });
    props.history.push({
      pathname: "/hire/settings/billing",
      state: {
        returnPath: props.location.pathname,
      },
    });
    props.onCancel();
  };

  return (
    <CenterModal headerTitleText={"Upgrade your plan"} onCancel={props.onCancel}>
      <>
        <Styled.Instructions>
          {`You have reached your limit of ${userLimit} active user${
            userLimit === 1 ? "" : "s"
          } on the ${currentPlanName} plan.`}
        </Styled.Instructions>
        <Styled.SecondaryMessage>{getSecondaryMessage()}</Styled.SecondaryMessage>
        <Styled.ButtonContainer>
          <Button name="viewPlansButton" type="submit" onClick={handleViewPlans}>
            View plans
          </Button>
          <Button styleType="secondary" onClick={props.onCancel}>
            Cancel
          </Button>
        </Styled.ButtonContainer>
      </>
    </CenterModal>
  );
}

export default UpgradeUserLimitModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Instructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: UpgradeUserLimitModal_Instructions;
    ${[t.mb(3)]}

    a {
      ${t.text.medium}
    }

    a:hover {
      text-decoration: underline;
    }
  `;
});

Styled.SecondaryMessage = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: UpgradeUserLimitModal_SecondaryMessage;
    ${[t.mb(5), t.text.sm]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: UpgradeUserLimitModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});
